// Couleur de marque d'un marchand (`Merchant.brandColor`). Le dashboard l'enregistre sans validation de
// format : une page ne l'applique qu'au format #RRGGBB, sinon elle garde ses propres couleurs.
const HEX = /^#?([0-9a-f]{6})$/i;
const WHITE = '#ffffff';
const NEAR_BLACK = '#111111';
const BLACK = '#000000';

/**
 * Fond des pages client de la refonte (#610), le même que `--client-bg` de app/tokens.css (vérifié par
 * express-page-web.spec.ts) : la couleur du commerce y est rendue lisible par `brandTextOn`.
 */
export const CLIENT_PAGE_BG = '#fbf6f0';

/** Couleur de marque utilisable (`#rrggbb`), ou `null` si la valeur n'est pas une couleur hexadécimale. */
export function brandColorOf(value: unknown): string | null {
  const m = typeof value === 'string' ? HEX.exec(value.trim()) : null;
  return m ? `#${m[1].toLowerCase()}` : null;
}

/** Texte lisible sur la couleur de la marque : sombre sur une couleur claire, blanc sinon. */
export function textOn(hex: string): string {
  const m = HEX.exec(hex.trim());
  if (!m) return WHITE;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? NEAR_BLACK : WHITE;
}

const channels = (hex: string): [number, number, number] | null => {
  const m = HEX.exec(hex.trim());
  return m ? ([0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) as [number, number, number]) : null;
};
const luminance = ([r, g, b]: [number, number, number]) => {
  const [lr, lg, lb] = [r, g, b].map((v) => v / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
};
const toHex = (rgb: number[]) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

/** Rapport de contraste WCAG entre deux couleurs `#rrggbb` (1 à 21) ; 1 si l'une n'est pas lisible. */
export function contrastRatio(a: string, b: string): number {
  const ca = channels(a);
  const cb = channels(b);
  if (!ca || !cb) return 1;
  const [hi, lo] = [luminance(ca), luminance(cb)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Variante « texte » de la couleur du commerce sur un fond (#596) : la couleur elle-même si elle atteint 4,5:1
 * (WCAG AA), sinon mêlée de noir (fond clair) ou de blanc (fond sombre), par pas de 5 %, jusqu'à 4,5:1. Pour les
 * pages client et les aperçus, seuls endroits où la couleur du commerce s'affiche ; jamais le chrome du dashboard.
 * Couleur du commerce illisible (le dashboard l'enregistre sans validation) : noir ou blanc selon le fond ; fond
 * illisible : `currentColor`, le texte environnant, lisible par construction.
 */
export function brandTextOn(brand: string, background: string, minRatio = 4.5): string {
  const c = channels(brand);
  const bg = channels(background);
  if (!bg) return 'currentColor';
  const toward = luminance(bg) > 0.18 ? 0 : 255;
  if (!c) return toward === 0 ? NEAR_BLACK : WHITE;
  for (let step = 0; step <= 20; step++) {
    const hex = toHex(c.map((v) => v + (toward - v) * (step / 20)));
    if (contrastRatio(hex, background) >= minRatio) return hex;
  }
  return toward === 0 ? BLACK : WHITE;
}
